'use client';

import { usePathname } from 'next/navigation';
import BuildingUpPage from './building-up-page';

// Routes that should be accessible even when website is disabled
const ALLOWED_ROUTES = [
  '/login',
  '/register-client',
  '/dispatcher-register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/dashboard',
  '/client-dashboard',
  // The client-facing equivalents of the routes above. They are sibling pages rather than
  // sub-paths, so `/client-forgot-password` does not match `/forgot-password` and each one has to
  // be named or clients lose account recovery whenever the site is switched off.
  '/client-register',
  '/client-forgot-password',
  '/client-reset-password',
  // OAuth hands control back to this path, so gating it breaks Google sign-in for everyone.
  '/auth',
  // Reached from a tokenised link in an email we sent.
  '/contract-review',
  '/privacy-policy',
  '/terms-of-service',
  '/cookie-policy',
  '/acceptable-use-policy',
  '/tracking',
  // Pages a customer reaches from a link we sent them. Gating these would break the delivery
  // notifications, which are sent whether or not the marketing site is switched on.
  '/proof',
  '/receipt',
  '/rate',
  '/shop',
  '/route-planner',
  '/contact',
  '/features',
  '/pricing',
  '/signup',
];

// Check if a path should be allowed
function isRouteAllowed(pathname: string): boolean {
  // Allow all dashboard and authenticated routes
  if (pathname.startsWith('/dashboard') || 
      pathname.startsWith('/client-dashboard') ||
      pathname.startsWith('/api') ||
      pathname.startsWith('/_next')) {
    return true;
  }

  // Check if path matches any allowed route
  return ALLOWED_ROUTES.some(route => pathname.startsWith(route));
}

interface WebsiteGateProps {
  children: React.ReactNode;
  previewUnlocked?: boolean;
}

export default function WebsiteGate({
  children,
  previewUnlocked = false,
}: WebsiteGateProps) {
  const pathname = usePathname();
  const isWebsiteEnabled = process.env.NEXT_PUBLIC_WEBSITE_ENABLED === 'true';

  // Public launch, or this browser already entered the preview code.
  if (isWebsiteEnabled || previewUnlocked) {
    return <>{children}</>;
  }

  // If website is disabled, check if current route is allowed
  const routeAllowed = isRouteAllowed(pathname);

  if (routeAllowed) {
    return <>{children}</>;
  }

  // Show building up page for all other routes
  return <BuildingUpPage />;
}

